import { useEffect, useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import { AuthState, initAuth, isAdminFromIdToken, signIn, tryAutoSignIn } from './auth';
import { getConfig, isProductionStage } from './config';
import { recordSession } from './api';
import OverridesTable from './OverridesTable';
import SchedulingTable from './SchedulingTable';
import PromptsPanel from './PromptsPanel';
import StatsPanel from './StatsPanel';

applyMode(Mode.Dark);

function NonProdBanner({ stage }: { stage: string }) {
  return (
    <div
      role="alert"
      style={{
        background: '#d13212',
        color: '#fff',
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '14px',
        letterSpacing: '0.5px',
        padding: '6px 12px',
        position: 'sticky',
        top: 0,
        zIndex: 5000,
      }}
    >
      ⚠️ {stage.toUpperCase()} — NON-PRODUCTION SYSTEM — data may be test data and can be modified or deleted at any time
    </div>
  );
}

type Page = 'scheduling' | 'overrides' | 'customization' | 'stats';

const PAGE_TITLES: Record<Page, string> = {
  scheduling: '📅 ASPEN — Scheduling',
  overrides: '📅 ASPEN — Tester Overrides',
  customization: '📅 ASPEN — Prompt Customization',
  stats: '📊 ASPEN — Usage Stats',
};

export default function App() {
  const [auth, setAuth] = useState<AuthState | null | undefined>(undefined);
  const [page, setPage] = useState<Page>('scheduling');
  const [stage, setStage] = useState('');

  useEffect(() => {
    initAuth().then(async (state) => {
      // Not signed in: try redirecting to Federate automatically (once per
      // browser session). Only show the sign-in page if that isn't possible.
      if (!state && (await tryAutoSignIn())) return; // redirecting away
      setAuth(state);
    });
    getConfig().then((c) => setStage(c.stage)).catch(() => setStage(''));
  }, []);

  // Record one adoption "connection" per app load, once the user is signed in.
  // Fire-and-forget: recordSession swallows its own errors.
  useEffect(() => {
    if (auth?.idToken) {
      void recordSession(auth.idToken);
    }
  }, [auth?.idToken]);

  const banner = !isProductionStage(stage) ? <NonProdBanner stage={stage} /> : null;

  if (auth === undefined) {
    return (
      <>
        {banner}
        <AppLayout
          content={<Box textAlign="center" padding="xxl">Loading...</Box>}
          navigationHide
          toolsHide
        />
      </>
    );
  }

  if (!auth) {
    return (
      <>
        {banner}
        <AppLayout
          navigationHide
          toolsHide
          content={
            <ContentLayout header={<Header variant="h1">📅 ASPEN</Header>}>
              <Box textAlign="center" padding="xxl">
                <SpaceBetween size="m">
                  <Box>You need to sign in to access ASPEN.</Box>
                  <Button variant="primary" onClick={() => void signIn()}>
                    Sign In with Amazon Federate
                  </Button>
                </SpaceBetween>
              </Box>
            </ContentLayout>
          }
        />
      </>
    );
  }

  // Show the stats dashboard only to admins (Federate `is_admin` claim). The
  // backend enforces the same on GET /stats, so this is just UI gating.
  const canViewStats = isAdminFromIdToken(auth.idToken);

  const navigation = (
    <SideNavigation
      header={{ text: 'ASPEN', href: '#' }}
      activeHref={`#/${page}`}
      onFollow={(e) => {
        e.preventDefault();
        const href = e.detail.href;
        if (href === '#/scheduling') setPage('scheduling');
        else if (href === '#/overrides') setPage('overrides');
        else if (href === '#/customization') setPage('customization');
        else if (href === '#/stats' && canViewStats) setPage('stats');
      }}
      items={[
        { type: 'link', text: 'Scheduling', href: '#/scheduling' },
        { type: 'link', text: 'Overrides', href: '#/overrides' },
        { type: 'link', text: 'Customization', href: '#/customization' },
        ...(canViewStats ? [{ type: 'link' as const, text: 'Usage Stats', href: '#/stats' }] : []),
      ]}
    />
  );

  return (
    <>
      {banner}
      <AppLayout
        navigation={navigation}
        toolsHide
        maxContentWidth={Number.MAX_VALUE}
        content={
          <ContentLayout
            header={
              <Header variant="h1" description={auth.email}>
                {PAGE_TITLES[page]}
              </Header>
            }
          >
            {page === 'scheduling' ? (
              <SchedulingTable idToken={auth.idToken} />
            ) : page === 'overrides' ? (
              <OverridesTable idToken={auth.idToken} />
            ) : page === 'stats' && canViewStats ? (
              <StatsPanel idToken={auth.idToken} />
            ) : page === 'stats' ? (
              // Non-authorized user somehow on the stats route: the backend
              // enforces access anyway, but never render the dashboard for them.
              <OverridesTable idToken={auth.idToken} />
            ) : (
              <PromptsPanel idToken={auth.idToken} />
            )}
          </ContentLayout>
        }
      />
    </>
  );
}
